import service from 'src/modules/auth/authService';
import Errors from 'src/modules/shared/error/errors';
import Message from 'src/view/shared/message';
import { i18n } from 'src/i18n';
import { getHistory } from 'src/modules/store';
import { AuthToken } from 'src/modules/auth/authToken';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import selectors from 'src/modules/auth/authSelectors';
import {
  parseAdminAuthError,
  adminAuthMessage,
} from 'src/modules/auth/adminAuthErrors';
import MfaChallengeStorage from 'src/modules/auth/mfaChallengeStorage';
import { tenantSubdomain } from '../tenant/tenantSubdomain';
import SettingsService from '../settings/settingsService';

const prefix = 'AUTH';

// The backend gates resend with a rolling ~60s cooldown that begins at sign-in
// (and restarts on each resend). It isn't advertised in the response, so we
// pre-arm the client countdown to avoid a wasted "please wait" click; the 429
// retryAfterSeconds remains the source of truth and corrects any drift.
const RESEND_COOLDOWN_SECONDS = 60;

const authActions = {
  ERROR_MESSAGE_CLEARED: `${prefix}_ERROR_MESSAGE_CLEARED`,

  AUTH_INIT_SUCCESS: `${prefix}_INIT_SUCCESS`,
  AUTH_INIT_ERROR: `${prefix}_INIT_ERROR`,

  AUTH_START: `${prefix}_START`,
  AUTH_SUCCESS: `${prefix}_SUCCESS`,
  AUTH_ERROR: `${prefix}_ERROR`,

  UPDATE_PROFILE_START: `${prefix}_UPDATE_PROFILE_START`,
  UPDATE_PROFILE_SUCCESS: `${prefix}_UPDATE_PROFILE_SUCCESS`,
  UPDATE_PROFILE_ERROR: `${prefix}_UPDATE_PROFILE_ERROR`,

  PASSWORD_CHANGE_START: `${prefix}_PASSWORD_CHANGE_START`,
  PASSWORD_CHANGE_SUCCESS: `${prefix}_PASSWORD_CHANGE_SUCCESS`,
  PASSWORD_CHANGE_ERROR: `${prefix}_PASSWORD_CHANGE_ERROR`,

  CURRENT_USER_REFRESH_START: `${prefix}_CURRENT_USER_REFRESH_START`,
  CURRENT_USER_REFRESH_SUCCESS: `${prefix}_CURRENT_USER_REFRESH_SUCCESS`,
  CURRENT_USER_REFRESH_ERROR: `${prefix}_CURRENT_USER_REFRESH_ERROR`,

  PASSWORD_RESET_EMAIL_START: `${prefix}_PASSWORD_RESET_EMAIL_START`,
  PASSWORD_RESET_EMAIL_SUCCESS: `${prefix}_PASSWORD_RESET_EMAIL_SUCCESS`,
  PASSWORD_RESET_EMAIL_ERROR: `${prefix}_PASSWORD_RESET_EMAIL_ERROR`,

  PASSWORD_RESET_START: `${prefix}_PASSWORD_RESET_START`,
  PASSWORD_RESET_SUCCESS: `${prefix}_PASSWORD_RESET_SUCCESS`,
  PASSWORD_RESET_ERROR: `${prefix}_PASSWORD_RESET_ERROR`,

  EMAIL_VERIFY_START: `${prefix}_EMAIL_VERIFY_START`,
  EMAIL_VERIFY_SUCCESS: `${prefix}_EMAIL_VERIFY_SUCCESS`,
  EMAIL_VERIFY_ERROR: `${prefix}_EMAIL_VERIFY_ERROR`,

  EMAIL_CONFIRMATION_START: `${prefix}_EMAIL_CONFIRMATION_START`,
  EMAIL_CONFIRMATION_SUCCESS: `${prefix}_EMAIL_CONFIRMATION_SUCCESS`,
  EMAIL_CONFIRMATION_ERROR: `${prefix}_EMAIL_CONFIRMATION_ERROR`,

  // Email-MFA login (two-step). The password step starts a challenge; the code
  // step (AUTH_SUCCESS on verify) issues the session.
  MFA_CHALLENGE_START: `${prefix}_MFA_CHALLENGE_START`,
  MFA_CHALLENGE_SUCCESS: `${prefix}_MFA_CHALLENGE_SUCCESS`,
  MFA_CHALLENGE_ERROR: `${prefix}_MFA_CHALLENGE_ERROR`,

  MFA_VERIFY_START: `${prefix}_MFA_VERIFY_START`,
  MFA_VERIFY_ERROR: `${prefix}_MFA_VERIFY_ERROR`,

  MFA_RESEND_START: `${prefix}_MFA_RESEND_START`,
  MFA_RESEND_SUCCESS: `${prefix}_MFA_RESEND_SUCCESS`,
  MFA_RESEND_ERROR: `${prefix}_MFA_RESEND_ERROR`,

  MFA_RESET: `${prefix}_MFA_RESET`,

  doClearErrorMessage() {
    return {
      type: authActions.ERROR_MESSAGE_CLEARED,
    };
  },

  doSendEmailConfirmation: () => async (
    dispatch,
    getState,
  ) => {
    try {
      dispatch({
        type: authActions.EMAIL_CONFIRMATION_START,
      });
      await service.sendEmailVerification();
      Message.success(
        i18n('auth.verificationEmailSuccess'),
      );
      dispatch({
        type: authActions.EMAIL_CONFIRMATION_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.EMAIL_CONFIRMATION_ERROR,
      });
    }
  },

  doSendPasswordResetEmail: (email) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_START,
      });
      await service.sendPasswordResetEmail(email);
      Message.success(
        i18n('auth.passwordResetEmailSuccess'),
      );
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_ERROR,
      });
    }
  },

  doRegisterEmailAndPassword: (email, password) => async (
    dispatch,
  ) => {
    try {
      dispatch({ type: authActions.AUTH_START });

      const token = await service.registerWithEmailAndPassword(
        email,
        password,
      );

      AuthToken.set(token, true);

      const currentUser = await service.fetchMe();

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser,
        },
      });
    } catch (error) {
      await service.signout();

      if (Errors.errorCode(error) !== 400) {
        Errors.handle(error);
      }

      dispatch({
        type: authActions.AUTH_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },

  // Step 1: email + password → backend emails a 6-digit code. No session yet.
  doAdminSignIn: (email, password) => async (dispatch) => {
    try {
      dispatch({ type: authActions.MFA_CHALLENGE_START });

      const {
        mfaToken,
        maskedEmail,
        expiresAt,
      } = await service.adminSignIn(email, password);

      const resendCooldownUntil =
        Date.now() + RESEND_COOLDOWN_SECONDS * 1000;

      MfaChallengeStorage.save({
        mfaToken,
        maskedEmail,
        expiresAt,
        resendCooldownUntil,
      });

      dispatch({
        type: authActions.MFA_CHALLENGE_SUCCESS,
        payload: {
          mfaToken,
          maskedEmail,
          expiresAt,
          resendCooldownUntil,
        },
      });
    } catch (error) {
      // adminAuthMessage maps INVALID_CREDENTIALS to a generic message, so the
      // password step never leaks which factor was wrong (§5).
      const { code } = parseAdminAuthError(error);
      dispatch({
        type: authActions.MFA_CHALLENGE_ERROR,
        payload: adminAuthMessage(code),
      });
    }
  },

  // Step 2: submit the code → backend issues the session JWT (+ remember-me
  // cookie when rememberMe is true).
  doVerifyMfaCode: (code, rememberMe) => async (
    dispatch,
    getState,
  ) => {
    const mfaToken = selectors.selectMfaToken(getState());

    try {
      dispatch({ type: authActions.MFA_VERIFY_START });

      const { token } = await service.adminMfaVerify(
        mfaToken,
        code,
        rememberMe,
      );

      // Persist the JWT only when the device is remembered; otherwise keep it
      // in memory so closing the tab requires the full flow again (§6).
      AuthToken.set(token, rememberMe);
      MfaChallengeStorage.clear();

      const currentUser = await service.fetchMe();

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: { currentUser },
      });
    } catch (error) {
      const {
        code: errorCode,
        remainingAttempts,
      } = parseAdminAuthError(error);

      // Dead challenge → bounce back to the password step.
      if (
        errorCode === 'MFA_TOO_MANY_ATTEMPTS' ||
        errorCode === 'MFA_CHALLENGE_CONSUMED'
      ) {
        MfaChallengeStorage.clear();
        dispatch({
          type: authActions.MFA_RESET,
          payload: {
            errorMessage: adminAuthMessage(errorCode),
          },
        });
        return;
      }

      // Wrong / expired code → keep the user on the code screen.
      dispatch({
        type: authActions.MFA_VERIFY_ERROR,
        payload: {
          errorMessage: adminAuthMessage(errorCode),
          remainingAttempts,
          expired: errorCode === 'MFA_CODE_EXPIRED',
        },
      });
    }
  },

  doResendMfaCode: () => async (dispatch, getState) => {
    const mfaToken = selectors.selectMfaToken(getState());

    try {
      dispatch({ type: authActions.MFA_RESEND_START });

      const { expiresAt } = await service.adminMfaResend(
        mfaToken,
      );

      const resendCooldownUntil =
        Date.now() + RESEND_COOLDOWN_SECONDS * 1000;

      MfaChallengeStorage.update({
        expiresAt,
        resendCooldownUntil,
      });

      dispatch({
        type: authActions.MFA_RESEND_SUCCESS,
        payload: { expiresAt, resendCooldownUntil },
      });

      Message.success(i18n('auth.mfa.resendSuccess'));
    } catch (error) {
      const {
        code,
        retryAfterSeconds,
      } = parseAdminAuthError(error);

      if (code === 'MFA_RESEND_COOLDOWN') {
        dispatch({
          type: authActions.MFA_RESEND_ERROR,
          payload: { cooldownSeconds: retryAfterSeconds },
        });
        return;
      }

      if (
        code === 'MFA_RESEND_LIMIT' ||
        code === 'MFA_CODE_EXPIRED'
      ) {
        MfaChallengeStorage.clear();
        dispatch({
          type: authActions.MFA_RESET,
          payload: { errorMessage: adminAuthMessage(code) },
        });
        return;
      }

      dispatch({
        type: authActions.MFA_RESEND_ERROR,
        payload: {},
      });
    }
  },

  // Cancel the challenge and return to the password step ("use a different
  // account", or after a dead challenge).
  doResetMfa: () => (dispatch) => {
    MfaChallengeStorage.clear();
    dispatch({ type: authActions.MFA_RESET });
  },

  doSignout: () => async (dispatch) => {
    try {
      dispatch({ type: authActions.AUTH_START });
      // Revoke the remember-me cookie server-side so the next visit is NOT
      // auto-logged-in, then drop the local JWT.
      await service.adminSignOut();

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser: null,
        },
      });
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.AUTH_ERROR,
      });
    }
  },

  doInit: () => async (dispatch) => {
    try {
      let token = AuthToken.get();
      let currentUser = null;

      if (!token) {
        // No local JWT → attempt silent auto-login from the HttpOnly
        // remember-me cookie. A 401 here (REMEMBER_ME_INVALID) is the normal
        // "first visit / not remembered" path — render login, no error.
        try {
          const data = await service.adminSessionRememberMe();
          token = data.token;
          AuthToken.set(token, true);
        } catch (rememberMeError) {
          token = null;
        }
      }

      if (token) {
        currentUser = await service.fetchMe();
      }

      dispatch({
        type: authActions.AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
        },
      });
    } catch (error) {
      service.signout();

      dispatch({
        type: authActions.AUTH_INIT_ERROR,
        payload: error,
      });
    }
  },

  doRefreshCurrentUser: () => async (dispatch) => {
    try {
      dispatch({
        type: authActions.CURRENT_USER_REFRESH_START,
      });

      let currentUser = null;
      const token = AuthToken.get();

      if (token) {
        currentUser = await service.fetchMe();
      }

      dispatch({
        type: authActions.CURRENT_USER_REFRESH_SUCCESS,
        payload: {
          currentUser,
        },
      });
    } catch (error) {
      service.signout();
      Errors.handle(error);

      dispatch({
        type: authActions.CURRENT_USER_REFRESH_ERROR,
        payload: error,
      });
    }
  },

  doUpdateProfile: (data) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.UPDATE_PROFILE_START,
      });

      await service.updateProfile(data);

      dispatch({
        type: authActions.UPDATE_PROFILE_SUCCESS,
      });
      await dispatch(authActions.doRefreshCurrentUser());
      Message.success(i18n('auth.profile.success'));
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.UPDATE_PROFILE_ERROR,
      });
    }
  },

  doChangePassword: (oldPassword, newPassword) => async (
    dispatch,
  ) => {
    try {
      dispatch({
        type: authActions.PASSWORD_CHANGE_START,
      });

      await service.changePassword(
        oldPassword,
        newPassword,
      );

      dispatch({
        type: authActions.PASSWORD_CHANGE_SUCCESS,
      });
      await dispatch(authActions.doRefreshCurrentUser());
      Message.success(i18n('auth.passwordChange.success'));
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.PASSWORD_CHANGE_ERROR,
      });
    }
  },

  doChangePasswordForMember: (user, newPassword) => async (
    dispatch,
  ) => {
    try {
      dispatch({
        type: authActions.PASSWORD_CHANGE_START,
      });

      await service.changePasswordMember(user, newPassword);

      dispatch({
        type: authActions.PASSWORD_CHANGE_SUCCESS,
      });
      await dispatch(authActions.doRefreshCurrentUser());
      Message.success(
        i18n('auth.passwordChangeMember.success'),
      );
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.PASSWORD_CHANGE_ERROR,
      });
    }
  },

  doVerifyEmail: (token) => async (dispatch, getState) => {
    try {
      const isLoading = selectors.selectLoadingVerifyEmail(
        getState(),
      );

      if (isLoading) {
        return;
      }

      dispatch({
        type: authActions.EMAIL_VERIFY_START,
      });

      await service.verifyEmail(token);

      await dispatch(authActions.doRefreshCurrentUser());

      dispatch({
        type: authActions.EMAIL_VERIFY_SUCCESS,
      });

      getHistory().push('/');
    } catch (error) {
      dispatch({
        type: authActions.EMAIL_VERIFY_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },

  doResetPassword: (token, password) => async (
    dispatch,
  ) => {
    try {
      dispatch({
        type: authActions.PASSWORD_RESET_START,
      });

      await service.passwordReset(token, password);

      Message.success(i18n('auth.passwordResetSuccess'));
      dispatch({
        type: authActions.PASSWORD_RESET_SUCCESS,
      });
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.PASSWORD_RESET_ERROR,
      });

      dispatch(authActions.doSignout());
      getHistory().push('/');
    }
  },

  doSelectTenant: (tenant) => async (dispatch) => {
    if (tenantSubdomain.isEnabled) {
      tenantSubdomain.redirectAuthenticatedTo(tenant.url);
      return;
    }

    AuthCurrentTenant.set(tenant);
    await dispatch(authActions.doRefreshCurrentUser());
    SettingsService.applyThemeFromTenant();
    getHistory().push('/');
  },
};

export default authActions;
